import {
	FormularioRelatorio,
	INPUTS_RELATORIOS_PCDS,
	PeriodosLabel,
} from './../../../interfaces/tipos-relatorios';
import {
	AfterViewInit,
	Component,
	inject,
	OnDestroy,
	OnInit,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { GroupButton } from '@componentes/public-button-group/public-button-group.component';
import { InstanciaRelatorio } from '@home/submodulos/dados-meteorologicos/interfaces/tipos-relatorios';
import { RelatoriosService } from '@home/submodulos/dados-meteorologicos/services/relatorios.service';
import { DocumentExporter } from '@utils/document-exporter';
import * as pdfseira from '@utils/pdf-seira';
import { format, formatISO } from 'date-fns';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { DadosValoresExtremosResponse } from '../../../interfaces/valores-extremos';
import { DateTimeUtils } from '@utils/datetime-util';
import {
	baixarEAbrirPDF,
	isNotNuloOuUndefined,
	isNuloOuUndefined,
	numberToBrNumber,
} from '@utils';
import { Agrupamento } from '../../../interfaces';
import { Select } from '@layout/interfaces/select';
import { filter, Subject, Subscription, takeUntil } from 'rxjs';
import { corrigeDuplicacaoNome } from '@home/submodulos/dados-meteorologicos/utils';
import {
	getXLSXDefinitions,
	getXLSXSheetDefinitions,
	getXLSXSheetTitleDefinitions,
} from '@utils/xlsx-seira';
import * as ExcelTable from 'mr-excel';
import { ValoresExtremos } from '@home/submodulos/dados-meteorologicos/enum/ValoresExtremos';
import { alinharDado } from '../../../../../../../shared/utils/index';
import { NumberUtils } from '@utils/number-utils';
import {
	PERIODO_MAXIMO_EM_ANOS_GRAFICO_PCD,
	PERIODO_MAXIMO_EM_DIAS_GRAFICO_PCD,
	PERIODO_MAXIMO_EM_MESES_GRAFICO_PCD,
} from '../grafico-variaveis-meteorologicas/grafico-variaveis-meteorologicas.component';

@Component({
	selector: 'seira-valores-extremos-tabela',
	templateUrl: './valores-extremos-tabela.component.html',
	styleUrls: ['./valores-extremos-tabela.component.scss'],
})
export class ValoresExtremosTabelaComponent
	implements OnDestroy, InstanciaRelatorio, OnInit, AfterViewInit
{
	carregando = false;
	tituloTabela = '';
	agrupamentoTabela = '';
	inputs = inject(INPUTS_RELATORIOS_PCDS);
	_destroyed = new Subject();
	private inscricoes = new Subscription();
	dadosTabelaValoresExtremos: DadosValoresExtremosResponse[] = [];
	opcoesMunPostos: Select<string>[];
	mensagemDataMinima?: string;

	descricaoRelatorio =
		'Os valores extremos se referem aos valores máximos e mínimos de chuva, temperatura, umidade, pressão, radiação, evapotranspiração, velocidade do vento a 2 metros e velocidade do vento a 10 metros registrados em um determinado período, local ou região.';

	botoesDeExportacao: GroupButton[] = [
		{
			label: '.pdf',
			size: 'small',
			icon: 'ph-file-pdf',
			onClick: async () => {
				await this.exportarPDF(this.dadosTabelaValoresExtremos);
			},
		},
		{
			label: '.csv',
			size: 'small',
			icon: 'ph-file-csv',
			onClick: () => {
				this.exportarCSV(this.dadosTabelaValoresExtremos);
			},
		},
		{
			label: '.txt',
			size: 'small',
			icon: 'ph-file-text',
			onClick: () => {
				this.exportarTXT(this.dadosTabelaValoresExtremos);
			},
		},
		{
			label: '.xlsx',
			size: 'small',
			icon: 'ph-file-xls',
			onClick: () => this.exportarXLSX(),
		},
	];

	constructor(
		private toastr: ToastrService,
		private relatoriosService: RelatoriosService
	) {
		const todosMunicipiosPostos = this.inputs.municipioPostos;
		this.ouvirMudancasMensagemDataMin();
		this.ouvirMudancasPeriodoBusca();
		this.ouvirMudancasMunPosto();
		this.opcoesMunPostos = [
			{ value: '0', name: 'Estado completo' },
			...todosMunicipiosPostos,
		];
		this.periodoBusca = 'mensal_parcial';
	}

	private set periodoBusca(valor: string) {
		this.inputs.form
			.get(FormularioRelatorio.PERIODO_BUSCA)
			?.setValue(valor, { emitEvent: false });
	}

	private preencherDatas() {
		const hoje = new Date();
		this.dataInicio = moment(hoje)
			.subtract(PERIODO_MAXIMO_EM_DIAS_GRAFICO_PCD, 'days')
			.toDate();
		this.dataFim = hoje;
	}

	private ouvirMudancasMunPosto(): void {
		this.inputs.form
			.get(FormularioRelatorio.ESTACAO)
			?.valueChanges.subscribe(munPosto => {
				if (munPosto === '0') {
					this.mensagemDataMinima = undefined;
					return;
				}
				this.atualizarDatasByDataMinima();
			});
	}

	private ouvirMudancasMensagemDataMin(): void {
		this.inscricoes.add(
			this.inputs.triggerTrocaMensagemDataMin$
				.pipe(filter(e => isNotNuloOuUndefined(e)))
				.subscribe(mensagem => {
					this.mensagemDataMinima = mensagem;
				})
		);
	}

	private ouvirMudancasPeriodoBusca() {
		this.inscricoes.add(
			this.inputs.form
				.get(FormularioRelatorio.PERIODO_BUSCA)
				?.valueChanges.subscribe(() => {
					if (this.municipioPostoSelecionado !== '0') {
						this.atualizarDatasByDataMinima();
					}
					this.gerarRelatorio();
				})
		);
	}

	private atualizarDatasByDataMinima() {
		if (this.periodoBusca.endsWith('completo')) {
			if (this.periodoBusca.startsWith('mensal')) {
				this.inputs.form
					.get(FormularioRelatorio.PERIODO)
					?.setValue(this.getInicioDataByPeriodoBusca(this.dataMinimaAtual));
			} else {
				this.inputs.form
					.get(FormularioRelatorio.PERIODO)
					?.setValue(this.getInicioDataByPeriodoBusca(this.dataMinimaAtual));
			}
		} else {
			this.dataInicio = this.getInicioDataByPeriodoBusca(this.dataMinimaAtual);
			this.dataFim = this.getDataComAcrescimoByPeriodoBusca(this.dataInicio);
		}
	}

	private getDataComAcrescimoByPeriodoBusca(data: Date): Date {
		const mapeamento: Record<string, [number, moment.DurationInputArg2]> = {
			MENSAL_PARCIAL: [PERIODO_MAXIMO_EM_MESES_GRAFICO_PCD, 'month'],
			MENSAL_COMPLETO: [PERIODO_MAXIMO_EM_MESES_GRAFICO_PCD, 'month'],
			ANUAL_PARCIAL: [PERIODO_MAXIMO_EM_ANOS_GRAFICO_PCD, 'year'],
			ANUAL_COMPLETO: [PERIODO_MAXIMO_EM_ANOS_GRAFICO_PCD, 'year'],
		};
		const [amount, unit] = mapeamento[this.periodoBusca.toUpperCase()];
		return moment(data)
			.add(amount - 1, unit)
			.toDate();
	}

	private get dataMinimaAtual(): Date {
		return this.inputs.datasPrimeirasColetasPCDs.find(d => {
			return d.id.toString() === this.municipioPostoSelecionado;
		})!.dataColeta;
	}

	private get municipioPostoSelecionado() {
		return this.inputs.form.get(FormularioRelatorio.ESTACAO)?.value;
	}

	private get periodoBusca(): string {
		return this.inputs.form.get(FormularioRelatorio.PERIODO_BUSCA)?.value;
	}

	private getInicioDataByPeriodoBusca(data: Date): Date {
		const dataMoment = moment(data);
		console.log(this.periodoBusca);

		const mapeamento: Record<string, () => moment.Moment> = {
			MENSAL_PARCIAL: () => dataMoment.startOf('day'),
			MENSAL_COMPLETO: () => dataMoment.startOf('month'),
			ANUAL_PARCIAL: () => dataMoment.startOf('day'),
			ANUAL_COMPLETO: () => dataMoment.startOf('year'),
		};
		return mapeamento[this.periodoBusca.toUpperCase()]().toDate();
	}

	private get dataInicio(): Date {
		return this.inputs.form.get('dataInicio')?.value;
	}

	private get dataFim(): Date {
		return this.inputs.form.get('dataFim')?.value;
	}

	private set dataInicio(novaData: Date) {
		this.inputs.form.get('dataInicio')?.setValue(novaData);
	}

	private set dataFim(novaData: Date) {
		this.inputs.form.get('dataFim')?.setValue(novaData);
	}

	ngOnInit() {
		this.inputs.form
			.get(FormularioRelatorio.ESTACAO)
			?.setValue(this.opcoesMunPostos[0].value);
		this.inputs.form
			.get(FormularioRelatorio.PERIODO_BUSCA)
			?.setValue('mensal_parcial', { emitEvent: false });

		this.setValidatorsByAgrupamento(
			this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.value
		);
		this.setDatasByPeriodoBusca(
			this.inputs.form.get(FormularioRelatorio.PERIODO_BUSCA)?.value
		);
		this.setValidators();
		this.preencherDatas();
	}

	ngAfterViewInit(): void {
		this.gerarRelatorio();
	}

	ngOnDestroy() {
		this.inscricoes.unsubscribe();
		this.inputs.form.get(FormularioRelatorio.DATA_INICIO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.DATA_FIM)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.PERIODO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.PERIODO_BUSCA)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.ESTACAO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.MUNICIPIO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.MICRORREGIAO)?.clearValidators();
		this.inputs.form
			.get(FormularioRelatorio.PERIODO_BUSCA)
			?.setValue('mensal', { emitEvent: false });
		this._destroyed.next(undefined);
	}

	setValidatorsByAgrupamento(agrupamentoValue: string) {
		this.handleRemoveValidatorsFromFormControl(FormularioRelatorio.MUNICIPIO);
		this.handleRemoveValidatorsFromFormControl(FormularioRelatorio.ESTACAO);
		this.handleRemoveValidatorsFromFormControl(
			FormularioRelatorio.MICRORREGIAO
		);

		switch (agrupamentoValue) {
			case 'MUNICIPIO_POSTO':
				this.inputs.form
					?.get(FormularioRelatorio.ESTACAO)
					?.setValidators(Validators.required);
				break;
			case 'MICRORREGIAO':
				this.inputs.form
					?.get(FormularioRelatorio.MICRORREGIAO)
					?.setValidators(Validators.required);
				break;
			case 'MUNICIPIO':
				this.inputs.form
					?.get(FormularioRelatorio.MUNICIPIO)
					?.setValidators(Validators.required);
				break;
		}
	}

	setDatasByPeriodoBusca(periodoBuscaValue: PeriodosLabel | null) {
		const dataInicio = this.getFormItemValue(FormularioRelatorio.DATA_INICIO);
		const dataFim = this.getFormItemValue(FormularioRelatorio.DATA_FIM);

		if (dataInicio && dataFim) {
			switch (periodoBuscaValue) {
				case 'mensal_completo':
					const mesInput = moment(
						this.getFormItemValue(FormularioRelatorio.PERIODO)
					);
					const mesVigente = moment().startOf('month');
					if (mesInput.isSameOrAfter(mesVigente)) {
						const newDataFim = mesVigente.subtract(1, 'month').endOf('month');
						this.inputs.form
							.get(FormularioRelatorio.PERIODO)
							?.setValue(newDataFim);
					}
					break;
				case 'anual_completo':
					const anoInput = moment(
						this.getFormItemValue(FormularioRelatorio.PERIODO)
					);
					const anoVigente = moment().startOf('year');
					if (anoInput.isSameOrAfter(anoVigente)) {
						const newDataFim = anoVigente.subtract(1, 'year').endOf('year');
						this.inputs.form
							.get(FormularioRelatorio.PERIODO)
							?.setValue(newDataFim);
					}
					break;
			}
		}
	}

	setValidators() {
		this.inputs.form
			.get(FormularioRelatorio.AGRUPAMENTO)
			?.valueChanges.pipe(takeUntil(this._destroyed))
			.subscribe({
				next: value => {
					this.setValidatorsByAgrupamento(value);
				},
			});

		this.inputs.form
			.get(FormularioRelatorio.PERIODO_BUSCA)
			?.valueChanges.pipe(takeUntil(this._destroyed))
			.subscribe({
				next: (periodo: PeriodosLabel | null) => {
					this.setDatasByPeriodoBusca(periodo);
					this.setValidatorsPeriodo(periodo);
				},
			});

		this.setValidatorsPeriodo(
			this.inputs.form.get(FormularioRelatorio.PERIODO_BUSCA)?.value
		);
		this.inputs.form
			.get(FormularioRelatorio.PERIODO_BUSCA)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.AGRUPAMENTO)
			?.setValidators(Validators.required);
	}

	private setValidatorsPeriodo(periodo: PeriodosLabel | null) {
		const isPeriodoCompleto =
			periodo === 'mensal_completo' || periodo === 'anual_completo';

		if (isPeriodoCompleto) {
			this.inputs.form.get(FormularioRelatorio.DATA_INICIO)?.clearValidators();
			this.inputs.form.get(FormularioRelatorio.DATA_FIM)?.clearValidators();
			this.inputs.form
				.get(FormularioRelatorio.PERIODO)
				?.setValidators(Validators.required);
		} else {
			this.inputs.form
				.get(FormularioRelatorio.DATA_INICIO)
				?.setValidators(Validators.required);
			this.inputs.form
				.get(FormularioRelatorio.DATA_FIM)
				?.setValidators(Validators.required);
			this.inputs.form.get(FormularioRelatorio.PERIODO)?.clearValidators();
		}

		this.inputs.form
			.get(FormularioRelatorio.DATA_INICIO)
			?.updateValueAndValidity({ emitEvent: false });
		this.inputs.form
			.get(FormularioRelatorio.DATA_FIM)
			?.updateValueAndValidity({ emitEvent: false });
		this.inputs.form
			.get(FormularioRelatorio.PERIODO)
			?.updateValueAndValidity({ emitEvent: false });
	}

	handleRemoveValidatorsFromFormControl(data: FormularioRelatorio) {
		this.inputs.form.get(data)?.clearValidators();
		this.inputs.form.get(data)?.updateValueAndValidity({ emitEvent: false });
	}

	getFormItemValue(field: string) {
		return this.inputs.form.get(field)?.value;
	}

	public gerarRelatorio() {
		const posto = this.getFormItemValue(FormularioRelatorio.ESTACAO);
		const dataInicio = this.getFormItemValue(FormularioRelatorio.DATA_INICIO);
		const dataFim = this.getFormItemValue(FormularioRelatorio.DATA_FIM);
		const periodo = this.getFormItemValue(FormularioRelatorio.PERIODO);
		this.agrupamentoTabela = this.getFormItemValue(
			FormularioRelatorio.AGRUPAMENTO
		);
		const periodoBusca = this.getFormItemValue(
			FormularioRelatorio.PERIODO_BUSCA
		);

		this.tituloTabela = `Valores extremos - ${this.getPeriodo()}`;

		this.inputs.setLoading(true);
		this.carregando = true;

		let inicioParaEnviar = dataInicio;
		let fimParaEnviar = dataFim;

		if (periodoBusca === 'mensal_completo') {
			inicioParaEnviar = moment(periodo).startOf('month').toDate();
			fimParaEnviar = moment(periodo).endOf('month').toDate();
		}

		if (periodoBusca === 'anual_completo') {
			inicioParaEnviar = moment(periodo).startOf('year').toDate();
			fimParaEnviar = moment(periodo).endOf('year').toDate();
		}

		this.relatoriosService
			.buscarRelatorioValoresExtremos({
				diaInicio: formatISO(new Date(inicioParaEnviar)),
				diaFim: formatISO(new Date(fimParaEnviar)),
				agrupamento: this.agrupamentoTabela,
				tipoPeriodoValoresExtremos: String(periodoBusca).toUpperCase(),
				posto,
			})
			.subscribe({
				next: valoresExtremos => {
					this.dadosTabelaValoresExtremos = valoresExtremos;
					this.relatoriosService.atualizaVisibilidadeBotoesExportacao(
						valoresExtremos
					);
				},
				error: () => {
					this.toastr.error('Ocorreu um erro ao gerar o relatório');
					this.carregando = false;
					this.inputs.setLoading(false);
				},
				complete: () => {
					this.carregando = false;
					this.inputs.setLoading(false);
				},
			});
	}

	getPeriodo() {
		const tipoPeriodoValoresExtremos = this.inputs.form.get(
			FormularioRelatorio.PERIODO_BUSCA
		)?.value;
		let dataInicio = this.inputs.form.get(FormularioRelatorio.DATA_INICIO)
			?.value;
		let dataFim = this.inputs.form.get(FormularioRelatorio.DATA_FIM)?.value;
		const periodo = this.inputs.form.get(FormularioRelatorio.PERIODO)?.value;

		if (moment.isMoment(dataInicio)) {
			dataInicio = dataInicio.toDate();
		}
		if (moment.isMoment(dataFim)) {
			dataFim = dataFim.toDate();
		}

		switch (tipoPeriodoValoresExtremos.toUpperCase()) {
			case 'MENSAL_COMPLETO':
				return DateTimeUtils.formatarDataPeriodo(periodo, '', 'mensal', true);
			case 'ANUAL_COMPLETO':
				return DateTimeUtils.formatarDataPeriodo(periodo, '', 'anual', true);
			case 'MENSAL_PARCIAL':
				return DateTimeUtils.formatarDataPeriodo(dataInicio, dataFim, 'diario');
			case 'ANUAL_PARCIAL':
				return DateTimeUtils.formatarDataPeriodo(dataInicio, dataFim, 'diario');
			default:
				return DateTimeUtils.formatarDataPeriodo(periodo, '', 'anual', true);
		}
	}

	formatarData(data: string) {
		if (!data) {
			return '-';
		}
		return DateTimeUtils.formatarData(data, 'dd/MM/yyyy') || '-';
	}

	formatarNumero(numero: number) {
		if (numero === null || numero === undefined) {
			return '-';
		}
		return numberToBrNumber(numero, 1) || '-';
	}

	getAgrupamento() {
		const agrupamento = this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)
			?.value;
		return Agrupamento[agrupamento as keyof typeof Agrupamento] ?? '-';
	}

	async exportarPDF(
		dadosTabelaValoresExtremos: DadosValoresExtremosResponse[]
	) {
		const documentDefinition: any = await pdfseira.documentDefinitions();
		documentDefinition.pageOrientation = 'landscape';
		documentDefinition.pageSize = 'C2';
		documentDefinition.pageMargins = [20, 90, 20, 40];

		documentDefinition.content.push({
			text: `Valores Extremos - ${this.getPeriodo()}`,
			fontSize: 12,
			alignment: 'center',
			margin: [0, 10],
		});

		if (!dadosTabelaValoresExtremos.length) {
			documentDefinition.content.push({
				text: 'Nenhum dado encontrado na tabela',
				alignment: 'center',
				fontSize: 10,
				margin: [0, 10],
			});
		}

		const tableData = [
			[
				{
					text: this.getAgrupamento(),
					style: 'tableHeader',
					rowSpan: 2,
					alignment: 'center',
				},
				{
					text: 'Precipitação (mm)',
					style: 'tableHeader',
					colSpan: 4,
					alignment: 'center',
				},
				{},
				{},
				{},
				{
					text: 'Temperatura máxima do ar (ºC)',
					style: 'tableHeader',
					colSpan: 4,
					alignment: 'center',
				},
				{},
				{},
				{},
				{
					text: 'Temperatura máxima (ºC)',
					style: 'tableHeader',
					colSpan: 4,
					alignment: 'center',
				},
				{},
				{},
				{},
				{
					text: 'Temperatura mínima (ºC)',
					style: 'tableHeader',
					colSpan: 4,
					alignment: 'center',
				},
				{},
				{},
				{},
				{
					text: 'Umidade relativa do ar (%)',
					style: 'tableHeader',
					colSpan: 4,
					alignment: 'center',
				},
				{},
				{},
				{},
				{
					text: 'Pressão Atmosférica (hPa)',
					style: 'tableHeader',
					colSpan: 4,
					alignment: 'center',
				},
				{},
				{},
				{},
				{
					text: 'Radiação solar (W/m2)',
					style: 'tableHeader',
					colSpan: 4,
					alignment: 'center',
				},
				{},
				{},
				{},
				{
					text: 'Evapotranspiração (mm)',
					style: 'tableHeader',
					colSpan: 4,
					alignment: 'center',
				},
				{},
				{},
				{},
				{
					text: 'Velocidade do vento a 2 metros (m/s)',
					style: 'tableHeader',
					colSpan: 4,
					alignment: 'center',
				},
				{},
				{},
				{},
				{
					text: 'Velocidade do vento a 10 metros (m/s)',
					style: 'tableHeader',
					colSpan: 4,
					alignment: 'center',
				},
				{},
				{},
				{},
			],
			[
				'',
				{ text: 'Data', style: 'tableHeader', alignment: 'center' },
				{ text: 'Max', style: 'tableHeader', alignment: 'center' },
				{ text: 'Data', style: 'tableHeader', alignment: 'center' },
				{ text: 'Min', style: 'tableHeader', alignment: 'center' },
				{ text: 'Data', style: 'tableHeader', alignment: 'center' },
				{ text: 'Max', style: 'tableHeader', alignment: 'center' },
				{ text: 'Data', style: 'tableHeader', alignment: 'center' },
				{ text: 'Min', style: 'tableHeader', alignment: 'center' },
				{ text: 'Data', style: 'tableHeader', alignment: 'center' },
				{ text: 'Max', style: 'tableHeader', alignment: 'center' },
				{ text: 'Data', style: 'tableHeader', alignment: 'center' },
				{ text: 'Min', style: 'tableHeader', alignment: 'center' },
				{ text: 'Data', style: 'tableHeader', alignment: 'center' },
				{ text: 'Max', style: 'tableHeader', alignment: 'center' },
				{ text: 'Data', style: 'tableHeader', alignment: 'center' },
				{ text: 'Min', style: 'tableHeader', alignment: 'center' },
				{ text: 'Data', style: 'tableHeader', alignment: 'center' },
				{ text: 'Max', style: 'tableHeader', alignment: 'center' },
				{ text: 'Data', style: 'tableHeader', alignment: 'center' },
				{ text: 'Min', style: 'tableHeader', alignment: 'center' },
				{ text: 'Data', style: 'tableHeader', alignment: 'center' },
				{ text: 'Max', style: 'tableHeader', alignment: 'center' },
				{ text: 'Data', style: 'tableHeader', alignment: 'center' },
				{ text: 'Min', style: 'tableHeader', alignment: 'center' },
				{ text: 'Data', style: 'tableHeader', alignment: 'center' },
				{ text: 'Max', style: 'tableHeader', alignment: 'center' },
				{ text: 'Data', style: 'tableHeader', alignment: 'center' },
				{ text: 'Min', style: 'tableHeader', alignment: 'center' },
				{ text: 'Data', style: 'tableHeader', alignment: 'center' },
				{ text: 'Max', style: 'tableHeader', alignment: 'center' },
				{ text: 'Data', style: 'tableHeader', alignment: 'center' },
				{ text: 'Min', style: 'tableHeader', alignment: 'center' },
				{ text: 'Data', style: 'tableHeader', alignment: 'center' },
				{ text: 'Max', style: 'tableHeader', alignment: 'center' },
				{ text: 'Data', style: 'tableHeader', alignment: 'center' },
				{ text: 'Min', style: 'tableHeader', alignment: 'center' },
				{ text: 'Data', style: 'tableHeader', alignment: 'center' },
				{ text: 'Max', style: 'tableHeader', alignment: 'center' },
				{ text: 'Data', style: 'tableHeader', alignment: 'center' },
				{ text: 'Min', style: 'tableHeader', alignment: 'center' },
			],
		];

		dadosTabelaValoresExtremos.forEach(dados => {
			tableData.push([
				this.agrupamentoTabela === 'MUNICIPIO_POSTO'
					? corrigeDuplicacaoNome(dados.nome)
					: alinharDado(dados.nome, true),
				alinharDado(this.formatarData(dados.dataColetaMaxPrecipitacao), true),
				alinharDado(this.formatarNumero(dados.maxPrecipitacao), true),
				alinharDado(this.formatarData(dados.dataColetaMinPrecipitacao), true),
				alinharDado(this.formatarNumero(dados.minPrecipitacao), true),
				alinharDado(this.formatarData(dados.dataColetaMaxTemperaturaAr), true),
				alinharDado(this.formatarNumero(dados.maxTemperaturaAr), true),
				alinharDado(this.formatarData(dados.dataColetaMinTemperaturaAr), true),
				alinharDado(this.formatarNumero(dados.minTemperaturaAr), true),
				alinharDado(
					this.formatarData(dados.dataColetaMaxTemperaturaMaxima),
					true
				),
				alinharDado(this.formatarNumero(dados.maxTemperaturaMaxima), true),
				alinharDado(
					this.formatarData(dados.dataColetaMinTemperaturaMaxima),
					true
				),
				alinharDado(this.formatarNumero(dados.minTemperaturaMaxima), true),
				alinharDado(
					this.formatarData(dados.dataColetaMaxTemperaturaMinima),
					true
				),
				alinharDado(this.formatarNumero(dados.maxTemperaturaMinima), true),
				alinharDado(
					this.formatarData(dados.dataColetaMinTemperaturaMinima),
					true
				),
				alinharDado(this.formatarNumero(dados.minTemperaturaMinima), true),
				alinharDado(
					this.formatarData(dados.dataColetaMaxUmidadeRelativa),
					true
				),
				alinharDado(this.formatarNumero(dados.maxUmidadeRelativa), true),
				alinharDado(
					this.formatarData(dados.dataColetaMinUmidadeRelativa),
					true
				),
				alinharDado(this.formatarNumero(dados.minUmidadeRelativa), true),
				alinharDado(
					this.formatarData(dados.dataColetaMaxPressaoAtmosferica),
					true
				),
				alinharDado(this.formatarNumero(dados.maxPressaoAtmosferica), true),
				alinharDado(
					this.formatarData(dados.dataColetaMinPressaoAtmosferica),
					true
				),
				alinharDado(this.formatarNumero(dados.minPressaoAtmosferica), true),
				alinharDado(this.formatarData(dados.dataColetaMaxRadiacao), true),
				alinharDado(this.formatarNumero(dados.maxRadiacao), true),
				alinharDado(this.formatarData(dados.dataColetaMinRadiacao), true),
				alinharDado(this.formatarNumero(dados.minRadiacao), true),
				alinharDado(
					this.formatarData(dados.dataColetaMaxEvapotranspiracao),
					true
				),
				alinharDado(this.formatarNumero(dados.maxEvapotranspiracao), true),
				alinharDado(
					this.formatarData(dados.dataColetaMinEvapotranspiracao),
					true
				),
				alinharDado(this.formatarNumero(dados.minEvapotranspiracao), true),
				alinharDado(
					this.formatarData(dados.dataColetaMaxVelocidadeVento2m),
					true
				),
				alinharDado(this.formatarNumero(dados.maxVelocidadeVento2m), true),
				alinharDado(
					this.formatarData(dados.dataColetaMinVelocidadeVento2m),
					true
				),
				alinharDado(this.formatarNumero(dados.minVelocidadeVento2m), true),
				alinharDado(
					this.formatarData(dados.dataColetaMaxVelocidadeVento10m),
					true
				),
				alinharDado(this.formatarNumero(dados.maxVelocidadeVento10m), true),
				alinharDado(
					this.formatarData(dados.dataColetaMinVelocidadeVento10m),
					true
				),
				alinharDado(this.formatarNumero(dados.minVelocidadeVento10m), true),
			]);
		});

		documentDefinition.content.push({
			table: {
				headerRows: 2,
				body: tableData,
				layout: 'lightHorizontalLines',
				fontSize: 8,
			},
			layout: {
				fillColor: (rowIndex: number, node: any, columnIndex: number) => {
					return rowIndex > 1 ? null : '#DCDCDC';
				},
			},
		});
		baixarEAbrirPDF(
			`relatorio-valores-extremos-${this.getPeriodo()}`,
			documentDefinition
		);
	}
	getHeaders(): Array<{
		label: string;
		text: string;
		size: number;
		colSpan?: number;
	}> {
		return [
			{
				label: 'MunicipioPosto',
				text: 'Município/Posto',
				size: 24,
			},
			// Grupo Precipitação
			{
				label: 'Precipitacao_Header',
				text: 'Precipitação (mm)',
				size: 15,
			},
			{ label: 'Precipitacao_Data', text: 'Data', size: 15 },
			{ label: 'Precipitacao_Max', text: 'Max', size: 15 },
			{ label: 'Precipitacao_Min', text: 'Min', size: 15 },

			// Grupo Temperatura Máxima
			{
				label: 'TemperaturaMaxima_Header',
				text: 'Temperatura máxima do ar (°C)',
				size: 15,
			},
			{ label: 'TemperaturaMaxima_Data', text: 'Data', size: 15 },
			{ label: 'TemperaturaMaxima_Max', text: 'Max', size: 15 },
			{ label: 'TemperaturaMaxima_Min', text: 'Min', size: 15 },

			// Grupo Umidade Relativa
			{
				label: 'UmidadeRelativa_Header',
				text: 'Umidade relativa do ar (%)',
				size: 15,
			},
			{ label: 'UmidadeRelativa_Data', text: 'Data', size: 15 },
			{ label: 'UmidadeRelativa_Max', text: 'Max', size: 15 },
			{ label: 'UmidadeRelativa_Min', text: 'Min', size: 15 },

			// Grupo Pressão Atmosférica
			{
				label: 'PressaoAtmosferica_Header',
				text: 'Pressão Atmosférica (hPa)',
				size: 15,
			},
			{ label: 'PressaoAtmosferica_Data', text: 'Data', size: 15 },
			{ label: 'PressaoAtmosferica_Max', text: 'Max', size: 15 },
			{ label: 'PressaoAtmosferica_Min', text: 'Min', size: 15 },

			// Grupo Radiação Solar
			{
				label: 'RadiacaoSolar_Header',
				text: 'Radiação solar (W/m²)',
				size: 15,
			},
			{ label: 'RadiacaoSolar_Data', text: 'Data', size: 15 },
			{ label: 'RadiacaoSolar_Max', text: 'Max', size: 15 },
			{ label: 'RadiacaoSolar_Min', text: 'Min', size: 15 },

			// Grupo Evapotranspiração
			{
				label: 'Evapotranspiracao_Header',
				text: 'Evapotranspiração (mm)',
				size: 15,
			},
			{ label: 'Evapotranspiracao_Data', text: 'Data', size: 15 },
			{ label: 'Evapotranspiracao_Max', text: 'Max', size: 15 },
			{ label: 'Evapotranspiracao_Min', text: 'Min', size: 15 },

			// Grupo Vento 2m
			{
				label: 'VelocidadeVento2m_Header',
				text: 'Velocidade do vento a 2 metros (m/s)',
				size: 15,
			},
			{ label: 'VelocidadeVento2m_Data', text: 'Data', size: 15 },
			{ label: 'VelocidadeVento2m_Max', text: 'Max', size: 15 },
			{ label: 'VelocidadeVento2m_Min', text: 'Min', size: 15 },

			// Grupo Vento 10m
			{
				label: 'VelocidadeVento10m_Header',
				text: 'Velocidade do vento a 10 metros (m/s)',
				size: 15,
			},
			{ label: 'VelocidadeVento10m_Data', text: 'Data', size: 15 },
			{ label: 'VelocidadeVento10m_Max', text: 'Max', size: 15 },
			{ label: 'VelocidadeVento10m_Min', text: 'Min', size: 15 },
		];
	}

	exportarCSV(dadosTabelaValoresExtremos: DadosValoresExtremosResponse[]) {
		const tableData = [];

		tableData.push([
			'',
			'Precipitação (mm)',
			'',
			'',
			'',
			'Temperatura máxima do ar (ºC)',
			'',
			'',
			'',
			'Temperatura máxima (ºC)',
			'',
			'',
			'',
			'Temperatura mínima (ºC)',
			'',
			'',
			'',
			'Umidade relativa do ar (%)',
			'',
			'',
			'',
			'Pressão Atmosférica (hPa)',
			'',
			'',
			'',
			'Radiação solar (W/m2)',
			'',
			'',
			'',
			'Evapotranspiração (mm)',
			'',
			'',
			'',
			'Velocidade do vento a 2 metros (m/s)',
			'',
			'',
			'',
			'Velocidade do vento a 10 metros (m/s)',
			'',
			'',
			'',
		]);

		tableData.push([
			this.getAgrupamento(),
			'Data',
			'Max',
			'Data',
			'Min',
			'Data',
			'Max',
			'Data',
			'Min',
			'Data',
			'Max',
			'Data',
			'Min',
			'Data',
			'Max',
			'Data',
			'Min',
			'Data',
			'Max',
			'Data',
			'Min',
			'Data',
			'Max',
			'Data',
			'Min',
			'Data',
			'Max',
			'Data',
			'Min',
			'Data',
			'Max',
			'Data',
			'Min',
			'Data',
			'Max',
			'Data',
			'Min',
			'Data',
			'Max',
			'Data',
			'Min',
		]);

		dadosTabelaValoresExtremos.forEach(dados => {
			tableData.push([
				this.agrupamentoTabela === 'MUNICIPIO_POSTO'
					? corrigeDuplicacaoNome(dados.nome)
					: dados.nome,
				this.formatarData(dados.dataColetaMaxPrecipitacao),
				this.formatarNumero(dados.maxPrecipitacao),
				this.formatarData(dados.dataColetaMinPrecipitacao),
				this.formatarNumero(dados.minPrecipitacao),
				this.formatarData(dados.dataColetaMaxTemperaturaAr),
				this.formatarNumero(dados.maxTemperaturaAr),
				this.formatarData(dados.dataColetaMinTemperaturaAr),
				this.formatarNumero(dados.minTemperaturaAr),
				this.formatarData(dados.dataColetaMaxTemperaturaMaxima),
				this.formatarNumero(dados.maxTemperaturaMaxima),
				this.formatarData(dados.dataColetaMinTemperaturaMaxima),
				this.formatarNumero(dados.minTemperaturaMaxima),
				this.formatarData(dados.dataColetaMaxTemperaturaMinima),
				this.formatarNumero(dados.maxTemperaturaMinima),
				this.formatarData(dados.dataColetaMinTemperaturaMinima),
				this.formatarNumero(dados.minTemperaturaMinima),
				this.formatarData(dados.dataColetaMaxUmidadeRelativa),
				this.formatarNumero(dados.maxUmidadeRelativa),
				this.formatarData(dados.dataColetaMinUmidadeRelativa),
				this.formatarNumero(dados.minUmidadeRelativa),
				this.formatarData(dados.dataColetaMaxPressaoAtmosferica),
				this.formatarNumero(dados.maxPressaoAtmosferica),
				this.formatarData(dados.dataColetaMinPressaoAtmosferica),
				this.formatarNumero(dados.minPressaoAtmosferica),
				this.formatarData(dados.dataColetaMaxRadiacao),
				this.formatarNumero(dados.maxRadiacao),
				this.formatarData(dados.dataColetaMinRadiacao),
				this.formatarNumero(dados.minRadiacao),
				this.formatarData(dados.dataColetaMaxEvapotranspiracao),
				this.formatarNumero(dados.maxEvapotranspiracao),
				this.formatarData(dados.dataColetaMinEvapotranspiracao),
				this.formatarNumero(dados.minEvapotranspiracao),
				this.formatarData(dados.dataColetaMaxVelocidadeVento2m),
				this.formatarNumero(dados.maxVelocidadeVento2m),
				this.formatarData(dados.dataColetaMinVelocidadeVento2m),
				this.formatarNumero(dados.minVelocidadeVento2m),
				this.formatarData(dados.dataColetaMaxVelocidadeVento10m),
				this.formatarNumero(dados.maxVelocidadeVento10m),
				this.formatarData(dados.dataColetaMinVelocidadeVento10m),
				this.formatarNumero(dados.minVelocidadeVento10m),
			]);
		});

		DocumentExporter.gerarCSV(
			tableData,
			`relatorio-valores-extremos-${this.getPeriodo()}`
		);
	}

	exportarTXT(dadosTabelaValoresExtremos: DadosValoresExtremosResponse[]) {
		let txtData = '';

		dadosTabelaValoresExtremos.forEach(dados => {
			txtData +=
				`${this.getAgrupamento()}: ${
					this.agrupamentoTabela === 'MUNICIPIO_POSTO'
						? corrigeDuplicacaoNome(dados.nome)
						: dados.nome
				}\n` +
				`Data: ${format(
					new Date(dados.dataColetaMaxPrecipitacao),
					'dd/MM/yyyy HH:mm'
				)}\n` +
				`Máxima precipitação (mm): ${this.formatarNumero(
					dados.maxPrecipitacao
				)}\n` +
				`Data: ${format(
					new Date(dados.dataColetaMinPrecipitacao),
					'dd/MM/yyyy HH:mm'
				)}\n` +
				`Mínima precipitação (mm): ${this.formatarNumero(
					dados.minPrecipitacao
				)}\n` +
				`Data: ${format(
					new Date(dados.dataColetaMaxTemperaturaAr),
					'dd/MM/yyyy HH:mm'
				)}\n` +
				`Máxima temperatura do ar (ºC): ${this.formatarNumero(
					dados.maxTemperaturaAr
				)}\n` +
				`Data: ${format(
					new Date(dados.dataColetaMinTemperaturaAr),
					'dd/MM/yyyy HH:mm'
				)}\n` +
				`Mínima temperatura do ar (ºC): ${this.formatarNumero(
					dados.minTemperaturaAr
				)}\n` +
				`Data: ${format(
					new Date(dados.dataColetaMaxTemperaturaMaxima),
					'dd/MM/yyyy HH:mm'
				)}\n` +
				`Máxima temperatura máxima (ºC): ${this.formatarNumero(
					dados.maxTemperaturaMaxima
				)}\n` +
				`Data: ${format(
					new Date(dados.dataColetaMinTemperaturaMaxima),
					'dd/MM/yyyy HH:mm'
				)}\n` +
				`Mínima temperatura máxima (ºC): ${this.formatarNumero(
					dados.minTemperaturaMaxima
				)}\n` +
				`Data: ${format(
					new Date(dados.dataColetaMaxTemperaturaMinima),
					'dd/MM/yyyy HH:mm'
				)}\n` +
				`Máxima temperatura mínima (ºC): ${this.formatarNumero(
					dados.maxTemperaturaMinima
				)}\n` +
				`Data: ${format(
					new Date(dados.dataColetaMinTemperaturaMinima),
					'dd/MM/yyyy HH:mm'
				)}\n` +
				`Mínima temperatura mínima (ºC): ${this.formatarNumero(
					dados.minTemperaturaMinima
				)}\n` +
				`Data: ${format(
					new Date(dados.dataColetaMaxUmidadeRelativa),
					'dd/MM/yyyy HH:mm'
				)}\n` +
				`Máxima umidade relativa do ar (%): ${this.formatarNumero(
					dados.maxUmidadeRelativa
				)}\n` +
				`Data: ${format(
					new Date(dados.dataColetaMinUmidadeRelativa),
					'dd/MM/yyyy HH:mm'
				)}\n` +
				`Mínima umidade relativa do ar (%): ${this.formatarNumero(
					dados.minUmidadeRelativa
				)}\n` +
				`Data: ${format(
					new Date(dados.dataColetaMaxPressaoAtmosferica),
					'dd/MM/yyyy HH:mm'
				)}\n` +
				`Máxima pressão atmosférica (hPa): ${this.formatarNumero(
					dados.maxPressaoAtmosferica
				)}\n` +
				`Data: ${format(
					new Date(dados.dataColetaMinPressaoAtmosferica),
					'dd/MM/yyyy HH:mm'
				)}\n` +
				`Mínima pressão atmosférica (hPa): ${this.formatarNumero(
					dados.minPressaoAtmosferica
				)}\n` +
				`Data: ${format(
					new Date(dados.dataColetaMaxRadiacao),
					'dd/MM/yyyy HH:mm'
				)}\n` +
				`Máxima radiação solar (W/m2): ${this.formatarNumero(
					dados.maxRadiacao
				)}\n` +
				`Data: ${format(
					new Date(dados.dataColetaMinRadiacao),
					'dd/MM/yyyy HH:mm'
				)}\n` +
				`Mínima radiação solar (W/m2): ${this.formatarNumero(
					dados.minRadiacao
				)}\n` +
				`Data: ${format(
					new Date(dados.dataColetaMaxEvapotranspiracao),
					'dd/MM/yyyy HH:mm'
				)}\n` +
				`Máxima evapotranspiração (mm): ${this.formatarNumero(
					dados.maxEvapotranspiracao
				)}\n` +
				`Data: ${format(
					new Date(dados.dataColetaMinEvapotranspiracao),
					'dd/MM/yyyy HH:mm'
				)}\n` +
				`Mínima evapotranspiração (mm): ${this.formatarNumero(
					dados.minEvapotranspiracao
				)}\n` +
				`Data: ${format(
					new Date(dados.dataColetaMaxVelocidadeVento2m),
					'dd/MM/yyyy HH:mm'
				)}\n` +
				`Máxima velocidade do vento a 2 metros (m/s): ${this.formatarNumero(
					dados.maxVelocidadeVento2m
				)}\n` +
				`Data: ${format(
					new Date(dados.dataColetaMinVelocidadeVento2m),
					'dd/MM/yyyy HH:mm'
				)}\n` +
				`Mínima velocidade do vento a 2 metros (m/s): ${this.formatarNumero(
					dados.minVelocidadeVento2m
				)}\n` +
				`Data: ${format(
					new Date(dados.dataColetaMaxVelocidadeVento10m),
					'dd/MM/yyyy HH:mm'
				)}\n` +
				`Máxima velocidade do vento a 10 metros (m/s): ${this.formatarNumero(
					dados.maxVelocidadeVento10m
				)}\n` +
				`Data: ${format(
					new Date(dados.dataColetaMinVelocidadeVento10m),
					'dd/MM/yyyy HH:mm'
				)}\n` +
				`Mínima velocidade do vento a 10 metros (m/s): ${this.formatarNumero(
					dados.minVelocidadeVento10m
				)}\n\n`;
		});

		DocumentExporter.gerarTXT(
			txtData,
			`relatorio-valores-extremos-${this.getPeriodo()}`
		);
	}

	exportarXLSX() {
		const nomeArquivo = this.tituloTabela;
		const headers = this.getXlsxHeaders(this.dadosTabelaValoresExtremos);
		const data = this.retornarDadosParaXLSX(headers);

		const dataExcel = {
			...getXLSXDefinitions(),
			sheet: [
				{
					...getXLSXSheetDefinitions(
						this.getAgrupamento() === Agrupamento.MUNICIPIO_POSTO ? [0] : []
					),
					headers: headers,
					data: data,
					columns: [],
					title: {
						...getXLSXSheetTitleDefinitions(),
						text: nomeArquivo,
					},
				},
			],
			fileName: nomeArquivo,
		};

		ExcelTable.generateExcel(dataExcel);
	}

	getXlsxHeaders(dadosTabela: any[]) {
		const headers: Array<{ label: string; text: string; size: number }> = [];

		Object.keys(dadosTabela[0]).forEach(key => {
			if (key === 'agrupamento') {
				headers.push({
					label: 'nome',
					text: Agrupamento[this.agrupamentoTabela as keyof typeof Agrupamento],
					size: 24,
				});
			} else if (key === 'nome') {
				// Não é necessário para o título da coluna
			} else {
				// Verifica se é uma data de coleta (dataColetaMax/dataColetaMin)
				const dateMatch = key.match(/dataColeta(Max|Min)(.*)/);
				if (dateMatch) {
					headers.push({
						label: key,
						text: `Data`,
						size: 24,
					});
				} else {
					// Verifica se é um valor máximo ou mínimo (max/min)
					const valueMatch = key.match(/(max|min)(.*)/);
					if (valueMatch) {
						const tipo = valueMatch[1] === 'max' ? 'Máxima' : 'Mínima';
						const variavel: ValoresExtremos =
							ValoresExtremos[valueMatch[2] as keyof typeof ValoresExtremos];
						headers.push({
							label: key,
							text: `${tipo} ${this.primeiraLetraMinuscula(variavel)}`,
							size: 24,
						});
					}
				}
			}
		});

		return headers;
	}

	primeiraLetraMinuscula(str: string) {
		if (str) {
			return str.charAt(0).toLowerCase() + str.slice(1);
		}
		return str;
	}

	retornarDadosParaXLSX(colunas: any) {
		const tableData: any[] = this.dadosTabelaValoresExtremos.map(
			(dado: any) => {
				const novoObjeto: any = {};

				colunas.forEach((coluna: any) => {
					const atributo: any = coluna?.label;

					if (dado[atributo]) {
						if (atributo === 'nome') {
							return (novoObjeto[atributo] = corrigeDuplicacaoNome(
								dado[atributo]
							));
						}

						if (atributo.toLowerCase().startsWith('data')) {
							return (novoObjeto[atributo] = format(
								new Date(dado[atributo]),
								'dd/MM/yyyy'
							));
						}

						novoObjeto[atributo] = isNuloOuUndefined(dado[atributo])
							? '-'
							: NumberUtils.getIgnorandoCasasDecimais(dado[atributo]);
						return novoObjeto[atributo];
					} else {
						novoObjeto[atributo] = '-';
					}
				});

				return novoObjeto;
			}
		);

		return tableData;
	}
}
